/**
 * What the user sees of the open map's size (engine/services/map-size.js):
 * - "Map full" next to the map's title while it is full (the phone's top bar
 *   says it instead: interfaces/classic/mobile/index.js);
 * - a dialog when the map becomes full, or opens full: what still works and
 *   how to make room;
 * - a note once it passes 80%.
 * Each is said once per map until the map goes back below 80%.
 */
import { Events, on } from "../../../engine/core/events.js";
import { createModal, showModal, closeModal } from "./modals.js";
import { t } from "../../../shared/i18n/index.js";
import { escapeHtml } from "../../../engine/core/validation.js";
import { mapSizeInfo } from "../../../engine/services/map-size.js";

const DIALOG_ID = "map-full-notice";

/**
 * @param {Object} cbs
 * @param {() => string|null} cbs.getCurrentDocId
 * @param {() => boolean} cbs.isMapFull - the open map is full (Store.isMapFull)
 * @param {(message: string, options?: Object) => void} cbs.showToast
 */
export function initMapSizeNotice({ getCurrentDocId, isMapFull, showToast }) {
  const told = new Map(); // docId → the level already told: 'warning' or 'full'

  function updateBadge() {
    const full = isMapFull();
    let badge = document.getElementById("map-full-badge");
    if (!full) { badge?.remove(); return; }
    if (badge) return;
    const title = document.getElementById("doc-title-text");
    if (!title?.parentNode) return;
    badge = document.createElement("span");
    badge.id = "map-full-badge";
    badge.className = "read-only-badge";
    badge.title = t("app.mapSize.full");
    badge.textContent = t("app.mapSize.badge");
    title.parentNode.insertBefore(badge, title.nextSibling);
  }

  function showFullDialog() {
    if (document.getElementById(DIALOG_ID)) return;
    const modal = createModal({
      id: DIALOG_ID,
      title: t("app.mapSize.fullTitle"),
      content: `<p>${escapeHtml(t("app.mapSize.full"))}</p>`,
      buttons: [{ text: t("common.ok"), action: "ok", className: "modal-btn-primary" }],
    });
    modal.querySelector('[data-action="ok"]').addEventListener("click", () => closeModal(DIALOG_ID));
    document.body.appendChild(modal);
    showModal(DIALOG_ID);
  }

  function tell(docId, level, percent) {
    if (level === "normal") { told.delete(docId); return; }
    if (docId !== getCurrentDocId()) return;
    const before = told.get(docId);
    if (before === level || (level === "warning" && before === "full")) return;
    told.set(docId, level);
    if (level === "full") showFullDialog();
    else showToast(t("app.mapSize.warning", { percent }), { type: "warning", duration: 10000 });
  }

  on(Events.MAP_SIZE_CHANGED, ({ docId, level, percent }) => {
    updateBadge();
    tell(docId, level, percent);
  });
  // A map can be measured while it opens, before it is the current one.
  on(Events.DATA_LOADED, () => {
    updateBadge();
    const docId = getCurrentDocId();
    const info = mapSizeInfo(docId);
    if (info) tell(docId, info.level, info.percent);
  });
  on(Events.MODAL_CLOSE, ({ modalId }) => {
    if (modalId === DIALOG_ID) document.getElementById(DIALOG_ID)?.remove();
  });
}
